import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Button } from "../../../../components/Button";
import ConfirmModal from "../../../../components/ConfirmModal";
import * as Fonts from "../../../../components/Fonts";
import Input from "../../../../components/Form/Input/input";
import { TextArea } from "../../../../components/Form/Input/textArea";
import Label from "../../../../components/Form/Label";
import { Form, FormGroup } from "../../../../components/Form/styles";
import { ScreenHeader } from "../../../../components/SubHeader";
import { useAppState } from "../../../../contexts/App";
import { useSite } from "../../../../contexts/Site";
import { findKeepo } from "../../../../hooks/SitePath";
import { siteHasContent } from "../../../../model/pages";
import { AI_BUILD_STEPS, AiBuildState, api } from "../../../../services/api";
import { Card, CardFooter, Container, Section } from "../../../Settings/styles";
import CreationProgress from "../../components/CreationProgress";
import { BuildProgress } from "./BuildProgress";
import { Description } from "./styles";

/** The owner's words about the business, at least and at most (the API's AiBuild::ABOUT_MIN / ABOUT_MAX). */
const ABOUT_MIN = 10;
const ABOUT_MAX = 2000;

/** The editor's languages the API builds in; any other: Portuguese. */
const LANGUAGES = ["pt", "en", "es", "fr", "de", "it"];

const prefix = "pages.editor.aiBuild";

/** The reasons the API gives that have their own message; any other is "couldn't build it now". */
const KNOWN_ERRORS = ["ABOUT_TOO_SHORT", "ABOUT_TOO_LONG", "INVALID_INSTAGRAM", "RATE_LIMIT", "ALREADY_PUBLISHED"];

/** The reasons a failed build gives that have their own message. */
const KNOWN_BUILD_ERRORS = ["TIMEOUT"];

/** Polls in a row that may fail before giving up, and the times "done" may come with no draft yet. */
const POLL_ERRORS_MAX = 5;
const DONE_WITHOUT_DRAFT_MAX = 3;

/** The API times a build out after 900 s (AiBuild::STUCK_SECONDS); the screen waits a bit more than that, so it never gives up on a build the API still considers running (a retry would get ALREADY_RUNNING). */
export const BUILD_TIMEOUT_MS = 16 * 60 * 1000;

type Props = {
  /** How often the build is asked about while it runs (ms). */
  pollMs?: number;
  /** How long the screen follows a build before giving up (ms). */
  timeoutMs?: number;
};

/**
 * "Criar com IA" (from /editor/start): the owner's Instagram and words about the business; the API builds the Keepo
 * (pages, sections, texts, photos and look) in its queue while this screen shows each step, then the editor opens it.
 * Coming back while it builds (a reload) follows the same build.
 */
const AiBuildPage = ({ pollMs = 2000, timeoutMs = BUILD_TIMEOUT_MS }: Props) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { appState, setAppState } = useAppState();
  const { site, setSite } = useSite();
  const siteId = appState.editingSiteId as string;

  const [instagram, setInstagram] = useState("");
  const [about, setAbout] = useState("");
  const [state, setState] = useState<AiBuildState>();
  const [error, setError] = useState<string>();
  const [isStarting, setStarting] = useState(false);
  const [askingToReplace, setAskingToReplace] = useState(false);
  const building = state?.status === "queued" || state?.status === "running";
  const alive = useRef(true);
  const startedAt = useRef<number>();
  const pollErrors = useRef(0);
  const doneWithoutDraft = useRef(0);

  // on every mount, not only the first: StrictMode mounts, unmounts and mounts again, and a screen marked gone
  // would stop following the build
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  /** Starts following a build: its clock and its failure counts begin again. */
  const follow = (build: AiBuildState) => {
    startedAt.current = Date.now();
    pollErrors.current = 0;
    doneWithoutDraft.current = 0;
    setState(build);
  };

  // a build already running (this screen reopened) is followed
  useEffect(() => {
    api
      .getAiBuild(siteId)
      .then(
        current => alive.current && (current.status === "queued" || current.status === "running") && follow(current)
      )
      .catch(() => undefined);
  }, [siteId]);

  // while it builds: asked again every `pollMs`; done opens the editor with the Keepo built
  useEffect(() => {
    if (!building) {
      return;
    }
    startedAt.current ??= Date.now();
    const timer = setTimeout(async () => {
      const giveUp = (code: string) => {
        setState(undefined);
        setError(code);
      };
      // the API times a build out by itself; if it never says so, this screen stops waiting too
      if (Date.now() - (startedAt.current as number) > timeoutMs) {
        giveUp("TIMEOUT");
        return;
      }
      try {
        const next = await api.getAiBuild(siteId);
        if (!alive.current) return;
        pollErrors.current = 0;
        if (next.status === "done") {
          api.clearCacheSites();
          const keepo = findKeepo(await api.getSites(), siteId);
          if (!alive.current) return;
          if (keepo?.draft) {
            setSite(keepo.draft);
            setAppState({ ...appState, editingSiteModified: true });
            // its pages first: the home and the ones the AI made, each one to edit
            navigate("/editor/pages");
            return;
          }
          // the draft isn't there yet: asked again a few times, never opening the editor with the old Keepo
          doneWithoutDraft.current += 1;
          if (doneWithoutDraft.current >= DONE_WITHOUT_DRAFT_MAX) {
            giveUp("failed");
          } else {
            setState(current => (current ? { ...current } : current));
          }
          return;
        }
        if (next.status === "failed" || next.status === "none") {
          giveUp(next.error && KNOWN_BUILD_ERRORS.includes(next.error) ? next.error : "failed");
          return;
        }
        setState(next);
      } catch {
        if (!alive.current) return;
        // a poll that fails is tried again, a few times
        pollErrors.current += 1;
        if (pollErrors.current >= POLL_ERRORS_MAX) {
          giveUp("failed");
        } else {
          setState(current => (current ? { ...current } : current));
        }
      }
    }, pollMs);
    return () => clearTimeout(timer);
  }, [building, state, siteId, pollMs, timeoutMs, navigate, setSite, setAppState, appState]);

  const start = async (confirmed = false) => {
    setError(undefined);
    if (about.trim().length < ABOUT_MIN) {
      setError("ABOUT_TOO_SHORT");
      return;
    }
    // the AI builds over the draft: what the Keepo already has (sections, pages) is replaced, so it asks first
    if (!confirmed && siteHasContent(site)) {
      setAskingToReplace(true);
      return;
    }
    setAskingToReplace(false);
    const language = i18n.language?.slice(0, 2);
    setStarting(true);
    try {
      const started = await api.startAiBuild(siteId, {
        instagram: instagram.trim(),
        about: about.trim(),
        language: LANGUAGES.includes(language) ? language : "pt",
      });
      follow(started);
    } catch (e) {
      const code = axios.isAxiosError(e) ? e.response?.data?.code : undefined;
      if (axios.isAxiosError(e) && e.response?.status === 409 && code === "ALREADY_RUNNING") {
        // a double click or another tab started it first: that build is followed
        try {
          const running = await api.getAiBuild(siteId);
          if (running.status === "queued" || running.status === "running") {
            if (alive.current) follow(running);
            return;
          }
        } catch {
          // it shows the notice below
        }
        setError("ALREADY_RUNNING");
        return;
      }
      setError(KNOWN_ERRORS.includes(code) ? code : "failed");
    } finally {
      setStarting(false);
    }
  };

  const current = AI_BUILD_STEPS.indexOf(state?.step ?? "instagram");

  // The forms' layout (as Help's): a titled section, one card with the fields, the button in its footer
  return (
    <Container>
      {/* where the person is in the flow, on top; the screen's own header under it */}
      <CreationProgress step="template" />
      <ScreenHeader
        title={t(`${prefix}.title`)} // not while it builds: leaving would let another build or template start over it
        back={building ? undefined : { label: t(`${prefix}.back`), to: "/editor/start" }}
      />
      <Section>
        <Fonts.Label>{t(`${prefix}.section`)}</Fonts.Label>
        {building ? (
          <BuildProgress current={current} />
        ) : (
          <Form onSubmit={event => event.preventDefault()}>
            <Card>
              <Description>{t(`${prefix}.description`)}</Description>
              <FormGroup>
                <Input
                  id="aiBuildInstagram"
                  type="text"
                  autoCapitalize="none"
                  label={t(`${prefix}.instagram.label`)}
                  placeholder={t(`${prefix}.instagram.placeholder`)}
                  value={instagram}
                  maxLength={100}
                  ignoreLengthInfo
                  onChange={event => setInstagram(event.target.value)}
                />
              </FormGroup>
              <FormGroup>
                <TextArea
                  id="aiBuildAbout"
                  rows={7}
                  value={about}
                  placeholder={t(`${prefix}.about.placeholder`)}
                  maxLength={ABOUT_MAX}
                  onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                    setAbout(event.target.value);
                    // writing again answers "tell a bit more": that notice goes (the others stay until a new try)
                    if (error === "ABOUT_TOO_SHORT") setError(undefined);
                  }}
                />
                <Label
                  maxLength={ABOUT_MAX}
                  usedLength={about.length}
                  htmlFor="aiBuildAbout"
                  text={t(`${prefix}.about.label`)}
                />
              </FormGroup>
              <CardFooter>
                {error && (
                  <p className="error" role="alert">
                    {t(`${prefix}.errors.${error}`)}
                  </p>
                )}
                <Button type="INFO" size="MD" disabled={isStarting} $loading={isStarting} onClick={() => start()}>
                  {error === "failed" ? t(`${prefix}.retry`) : t(`${prefix}.submit`)}
                </Button>
              </CardFooter>
            </Card>
          </Form>
        )}
      </Section>
      {askingToReplace && (
        <ConfirmModal
          type="INFO"
          title={t(`${prefix}.replaceConfirm.title`)}
          descriptions={[t(`${prefix}.replaceConfirm.description`)]}
          confirmButtonTitle={t(`${prefix}.replaceConfirm.confirm`)}
          cancelTitle={t(`${prefix}.replaceConfirm.cancel`)}
          onConfirm={() => start(true)}
          onCancel={() => setAskingToReplace(false)}
        />
      )}
    </Container>
  );
};

export default AiBuildPage;
